import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "@/App";
import { toast } from "sonner";
import { safeRedirect } from "@/utils/api";
import BottomNav from "@/components/BottomNav";
import { 
  Bookmark, Plane, Hotel, MapPin, Clock,
  CreditCard, Wallet, CheckCircle, XCircle,
  ChevronLeft, Loader2, Target, Link, Unlink, ShieldCheck
} from "lucide-react";

export default function Reservations() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [goals, setGoals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [insurancePlans, setInsurancePlans] = useState({});
  const [selectedGoalId, setSelectedGoalId] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Check for payment completion
    const sessionId = searchParams.get("session_id");
    const paidId = searchParams.get("paid");
    
    if (sessionId || paidId) {
      toast.success("Deposit confirmed! Your deal is locked in 🎉");
      navigate("/reservations", { replace: true });
      fetchData();
    }
  }, [searchParams, navigate]);

  const fetchData = async () => {
    try {
      const [userRes, reservationsRes, goalsRes] = await Promise.all([
        fetch(`${API}/auth/me`, { credentials: "include" }),
        fetch(`${API}/payments/reservations`, { credentials: "include" }),
        fetch(`${API}/goals/`, { credentials: "include" })
      ]);
      
      if (userRes.ok) setUser(await userRes.json());
      if (reservationsRes.ok) setReservations(await reservationsRes.json());
      if (goalsRes.ok) setGoals(await goalsRes.json());
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to load reservations. Please refresh.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayRemaining = async (useWallet = false) => {
    if (!selectedReservation) return;
    
    setIsProcessing(true);
    try {
      const response = await fetch(
        `${API}/payments/reservations/${selectedReservation.reservation_id}/pay-remaining`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            origin_url: window.location.origin,
            use_wallet: useWallet
          })
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Payment failed");
      }
      
      if (useWallet) {
        toast.success(data.message);
        setShowPayModal(false);
        fetchData();
      } else {
        safeRedirect(data.url);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = async (reservationId) => {
    if (!window.confirm("Cancel this reservation? You'll receive 80% of your deposit back.")) return;
    
    try {
      const response = await fetch(
        `${API}/payments/reservations/${reservationId}/cancel`,
        {
          method: "POST",
          credentials: "include"
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Cancellation failed");
      }
      
      toast.success(data.message);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleLinkGoal = async () => {
    if (!selectedReservation || !selectedGoalId) return;
    
    setIsProcessing(true);
    try {
      const response = await fetch(
        `${API}/payments/reservations/${selectedReservation.reservation_id}/link-goal`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ goal_id: selectedGoalId })
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to link goal");
      }
      
      toast.success(data.message);
      setShowLinkModal(false);
      setSelectedGoalId("");
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUnlinkGoal = async (reservationId) => {
    try {
      const response = await fetch(
        `${API}/payments/reservations/${reservationId}/unlink-goal`,
        {
          method: "POST",
          credentials: "include"
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to unlink goal");
      }
      
      toast.success(data.message);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getLinkedGoal = (goalId) => {
    return goals.find(g => g.goal_id === goalId);
  };

  const openInsuranceModal = async (reservation) => {
    setSelectedReservation(reservation);
    try {
      const res = await fetch(`${API}/payments/insurance/plans`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setInsurancePlans(data.plans || {});
      }
    } catch (e) {
      console.error("Insurance plans error:", e);
    }
    setShowInsuranceModal(true);
  };

  const handlePurchaseInsurance = async (planId) => {
    if (!selectedReservation) return;
    setIsProcessing(true);
    try {
      const response = await fetch(`${API}/payments/insurance/purchase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          plan_id: planId,
          reservation_id: selectedReservation.reservation_id,
          origin_url: window.location.origin
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Failed to start checkout");
      safeRedirect(data.url);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "reserved":
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
            <Clock className="w-3 h-3" /> Reserved
          </span>
        );
      case "paid":
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
            <CheckCircle className="w-3 h-3" /> Confirmed
          </span>
        );
      case "cancelled":
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
            <XCircle className="w-3 h-3" /> Cancelled
          </span>
        );
      default:
        return null;
    }
  };

  const getDealIcon = (type) => {
    switch (type) {
      case "flight": return Plane;
      case "hotel": return Hotel;
      default: return MapPin;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 safe-bottom" data-testid="reservations-skeleton">
        <div className="bg-white border-b border-slate-100 px-6 py-4">
          <div className="h-6 w-36 bg-slate-200 rounded animate-pulse mb-1" />
          <div className="h-3 w-24 bg-slate-100 rounded animate-pulse" />
        </div>
        <div className="px-6 py-6 space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-slate-200 animate-pulse" />
                <div className="flex-1">
                  <div className="h-5 w-3/4 bg-slate-200 rounded animate-pulse mb-2" />
                  <div className="h-3 w-1/2 bg-slate-100 rounded animate-pulse mb-1" />
                  <div className="h-3 w-1/3 bg-slate-100 rounded animate-pulse" />
                </div>
              </div>
              <div className="flex gap-2">
                <div className="h-9 flex-1 bg-slate-100 rounded-full animate-pulse" />
                <div className="h-9 flex-1 bg-sky-100 rounded-full animate-pulse" />
              </div>
            </div>
          ))}
        </div>
        <BottomNav />
      </div>
    );
  }

  const activeReservations = reservations.filter(r => r.status === "reserved");
  const completedReservations = reservations.filter(r => r.status === "paid");
  const cancelledReservations = reservations.filter(r => r.status === "cancelled");

  return (
    <div className="min-h-screen bg-slate-50 safe-bottom" data-testid="reservations-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 px-6 py-4 sticky top-0 z-40">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/deals")} className="text-slate-600">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">My Reservations</h1>
            <p className="text-sm text-slate-500">Lock in deals while you save</p>
          </div>
        </div>
      </header>

      <main className="px-6 py-6">
        {reservations.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 rounded-full bg-sky-100 flex items-center justify-center mx-auto mb-6">
              <Bookmark className="w-12 h-12 text-sky-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">No reservations yet</h2>
            <p className="text-slate-500 mb-6">
              Reserve deals with a small deposit while you save up!
            </p>
            <Button 
              onClick={() => navigate("/deals")}
              className="savecation-button"
            >
              Browse Deals
            </Button>
          </div>
        ) : (
          <div className="space-y-8 mb-24">
            {/* Active Reservations */}
            {activeReservations.length > 0 && (
              <section>
                <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-500" />
                  Active Reservations
                </h2>
                <div className="space-y-4">
                  {activeReservations.map(reservation => (
                    <ReservationCard
                      key={reservation.reservation_id}
                      reservation={reservation}
                      linkedGoal={getLinkedGoal(reservation.goal_id)}
                      onPayRemaining={() => {
                        setSelectedReservation(reservation);
                        setShowPayModal(true);
                      }}
                      onCancel={() => handleCancel(reservation.reservation_id)}
                      onLinkGoal={() => {
                        setSelectedReservation(reservation);
                        setSelectedGoalId(reservation.goal_id || "");
                        setShowLinkModal(true);
                      }}
                      onUnlinkGoal={() => handleUnlinkGoal(reservation.reservation_id)}
                      onInsurance={() => openInsuranceModal(reservation)}
                      getDealIcon={getDealIcon}
                      getStatusBadge={getStatusBadge}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Completed */}
            {completedReservations.length > 0 && (
              <section>
                <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  Confirmed Trips
                </h2>
                <div className="space-y-4">
                  {completedReservations.map(reservation => (
                    <ReservationCard
                      key={reservation.reservation_id}
                      reservation={reservation}
                      linkedGoal={getLinkedGoal(reservation.goal_id)}
                      getDealIcon={getDealIcon}
                      getStatusBadge={getStatusBadge}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Cancelled */}
            {cancelledReservations.length > 0 && (
              <section>
                <h2 className="text-lg font-bold text-slate-500 mb-4">
                  Cancelled
                </h2>
                <div className="space-y-4 opacity-60">
                  {cancelledReservations.map(reservation => (
                    <ReservationCard
                      key={reservation.reservation_id}
                      reservation={reservation}
                      getDealIcon={getDealIcon}
                      getStatusBadge={getStatusBadge}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>

      {/* Pay Remaining Modal */}
      <Dialog open={showPayModal} onOpenChange={setShowPayModal}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Complete Your Booking</DialogTitle>
          </DialogHeader>
          {selectedReservation && (
            <div className="space-y-4 mt-4">
              <div className="bg-slate-50 rounded-2xl p-4">
                <p className="text-sm text-slate-500 mb-1">Remaining Balance</p>
                <p className="text-3xl font-bold text-slate-900">
                  ${selectedReservation.remaining_balance?.toFixed(2)}
                </p>
                <div className="mt-3">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-500">Deposit paid</span>
                    <span className="font-medium">${selectedReservation.deposit_amount?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Total</span>
                    <span className="font-medium">${selectedReservation.total_amount?.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {user?.wallet_balance >= selectedReservation.remaining_balance && (
                <Button
                  onClick={() => handlePayRemaining(true)}
                  disabled={isProcessing}
                  className="w-full h-12 rounded-full bg-emerald-500 hover:bg-emerald-600 font-bold"
                  data-testid="pay-with-wallet"
                >
                  {isProcessing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Wallet className="w-5 h-5 mr-2" />
                      Pay with Wallet (${user.wallet_balance.toFixed(2)} available)
                    </>
                  )}
                </Button>
              )}

              <Button
                onClick={() => handlePayRemaining(false)}
                disabled={isProcessing}
                variant={user?.wallet_balance >= selectedReservation.remaining_balance ? "outline" : "default"}
                className={`w-full h-12 rounded-full font-bold ${
                  user?.wallet_balance >= selectedReservation.remaining_balance 
                    ? "" 
                    : "savecation-button"
                }`}
                data-testid="pay-with-card"
              >
                {isProcessing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Pay with Card
                  </>
                )}
              </Button>

              {user?.wallet_balance < selectedReservation.remaining_balance && (
                <p className="text-sm text-center text-slate-500">
                  Need ${(selectedReservation.remaining_balance - user.wallet_balance).toFixed(2)} more in wallet to pay directly
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Link Goal Modal */}
      <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Target className="w-6 h-6 text-sky-500" />
              Link to Savings Goal
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-slate-600">
              Link this reservation to a savings goal to track your progress towards this trip.
            </p>
            
            {goals.filter(g => g.status === "active").length === 0 ? (
              <div className="bg-slate-50 rounded-2xl p-4 text-center">
                <Target className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                <p className="text-slate-600">No active goals yet</p>
                <Button
                  variant="outline"
                  onClick={() => navigate("/goals")}
                  className="mt-3 rounded-full"
                >
                  Create a Goal
                </Button>
              </div>
            ) : (
              <>
                <Select value={selectedGoalId} onValueChange={setSelectedGoalId}>
                  <SelectTrigger className="h-12 rounded-2xl">
                    <SelectValue placeholder="Select a goal..." />
                  </SelectTrigger>
                  <SelectContent>
                    {goals.filter(g => g.status === "active").map(goal => (
                      <SelectItem key={goal.goal_id} value={goal.goal_id}>
                        <div className="flex items-center gap-2">
                          <Target className="w-4 h-4 text-sky-500" />
                          <span>{goal.title}</span>
                          <span className="text-slate-500">
                            (${goal.current_amount.toFixed(0)} / ${goal.target_amount.toFixed(0)})
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button
                  onClick={handleLinkGoal}
                  disabled={isProcessing || !selectedGoalId}
                  className="savecation-button w-full"
                >
                  {isProcessing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Link className="w-5 h-5 mr-2" />
                      Link Goal
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Insurance Modal */}
      <Dialog open={showInsuranceModal} onOpenChange={setShowInsuranceModal}>
        <DialogContent className="sm:max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-sky-500" /> Travel Insurance
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 mb-4">Protect your trip with our travel insurance plans</p>
          <div className="space-y-4">
            {Object.entries(insurancePlans).map(([planId, plan]) => (
              <div key={planId} className={`p-4 rounded-2xl border-2 transition-colors ${
                planId === "premium" ? "border-sky-300 bg-sky-50" : "border-slate-100"
              }`}>
                {planId === "premium" && (
                  <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-sky-500 text-white mb-2">RECOMMENDED</span>
                )}
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-slate-900">{plan.name}</h3>
                  <span className="text-xl font-bold text-sky-600">${plan.price}</span>
                </div>
                <p className="text-xs text-slate-500 mb-3">Up to ${plan.coverage.toLocaleString()} coverage</p>
                <ul className="space-y-1 mb-3">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => handlePurchaseInsurance(planId)}
                  disabled={isProcessing}
                  variant={planId === "premium" ? "default" : "outline"}
                  className={`w-full rounded-full ${planId === "premium" ? "savecation-button" : ""}`}
                  data-testid={`insurance-${planId}-btn`}
                >
                  {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : `Get ${plan.name}`}
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
}

function ReservationCard({ reservation, linkedGoal, onPayRemaining, onCancel, onLinkGoal, onUnlinkGoal, onInsurance, getDealIcon, getStatusBadge }) {
  const deal = reservation.deal_details || {};
  const Icon = getDealIcon(deal.type);
  const progress = ((reservation.deposit_amount / reservation.total_amount) * 100) || 0;
  
  // Calculate days until expiration
  const getDaysLeft = () => {
    if (!reservation.expires_at) return null;
    const expires = new Date(reservation.expires_at);
    const now = new Date();
    const diff = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));
    return diff;
  };
  
  const daysLeft = getDaysLeft();

  return (
    <div className="bg-white rounded-3xl border-2 border-slate-100 overflow-hidden" data-testid={`reservation-${reservation.reservation_id}`}>
      {/* Deal Image */}
      {deal.image && (
        <div className="h-32 relative">
          <img src={deal.image} alt={deal.name || deal.destination} className="w-full h-full object-cover" />
          <div className="absolute top-3 right-3">
            {getStatusBadge(reservation.status)}
          </div>
          {reservation.status === "reserved" && daysLeft !== null && daysLeft <= 7 && (
            <div className="absolute bottom-3 left-3 px-3 py-1 rounded-full bg-amber-500 text-white text-xs font-semibold">
              {daysLeft} days left
            </div>
          )}
        </div>
      )}
      
      <div className="p-5">
        {/* Deal Info */}
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-sky-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-slate-900">
              {deal.name || `${deal.origin} → ${deal.destination}`}
            </h3>
            <p className="text-sm text-slate-500">
              {deal.location || deal.airline}
            </p>
          </div>
        </div>

        {/* Linked Goal */}
        {reservation.status === "reserved" && (
          <div className="mb-4">
            {linkedGoal ? (
              <div className="flex items-center justify-between p-3 bg-sky-50 rounded-xl">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-sky-500" />
                  <span className="text-sm font-medium text-slate-700">{linkedGoal.title}</span>
                  <span className="text-xs text-slate-500">
                    ${linkedGoal.current_amount.toFixed(0)} saved
                  </span>
                </div>
                <button
                  onClick={onUnlinkGoal}
                  className="text-slate-400 hover:text-rose-500"
                  title="Unlink goal"
                >
                  <Unlink className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={onLinkGoal}
                className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl w-full text-left hover:bg-slate-100 transition-colors"
              >
                <Link className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-500">Link to a savings goal</span>
              </button>
            )}
          </div>
        )}

        {/* Progress */}
        {reservation.status === "reserved" && (
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-500">Paid so far</span>
              <span className="font-semibold text-sky-600">
                ${reservation.deposit_amount?.toFixed(0)} / ${reservation.total_amount?.toFixed(0)}
              </span>
            </div>
            <div className="reserve-progress">
              <div 
                className="reserve-progress-fill"
                style={{ width: `${progress}%` }}
              >
                <span className="reserve-progress-text">{Math.round(progress)}%</span>
              </div>
            </div>
            <p className="text-sm text-slate-500 mt-2">
              ${reservation.remaining_balance?.toFixed(2)} remaining
            </p>
          </div>
        )}

        {/* Actions */}
        {reservation.status === "reserved" && (
          <div className="space-y-2">
            <div className="flex gap-3">
              <Button
                onClick={onPayRemaining}
                className="flex-1 savecation-button h-10"
                data-testid={`pay-${reservation.reservation_id}`}
              >
                Pay Remaining
              </Button>
              <Button
                onClick={onCancel}
                variant="outline"
                className="h-10 px-4 rounded-full text-rose-600 border-rose-200 hover:bg-rose-50"
                data-testid={`cancel-${reservation.reservation_id}`}
              >
                Cancel
              </Button>
            </div>
            {onInsurance && (
              <Button
                onClick={onInsurance}
                variant="outline"
                className="w-full h-9 rounded-full text-sm border-sky-200 text-sky-600 hover:bg-sky-50"
                data-testid={`insurance-${reservation.reservation_id}`}
              >
                <ShieldCheck className="w-4 h-4 mr-2" /> Add Travel Insurance
              </Button>
            )}
          </div>
        )}

        {reservation.status === "paid" && (
          <div className="bg-emerald-50 rounded-xl p-3 text-center">
            <p className="text-emerald-700 font-semibold">🎉 Trip Confirmed!</p>
            <p className="text-sm text-emerald-600">Get ready for your adventure</p>
          </div>
        )}
      </div>
    </div>
  );
}
